import React from 'react';
import { Link } from 'react-router-dom';
import { Wine, Sparkle, Drop, Palette, Thermometer, Clock, CaretRight } from '@phosphor-icons/react';

const WineStylesPage = () => {
  const wineStyles = [
    {
      category: 'Vins Rouges',
      icon: <Wine size={32} color="#4B164C" weight="light" />,
      description: 'Vins élaborés à partir de raisins rouges avec macération des peaux',
      styles: [
        {
          name: 'Légers et Fruités',
          examples: ['Beaujolais', 'Pinot Noir jeune', 'Gamay'],
          characteristics: 'Tanins doux, acidité vive, fruits rouges frais',
          serving: '12-14°C',
          aging: '2-3 ans',
          food: 'Charcuteries, fromages doux, volailles'
        },
        {
          name: 'Moyens et Équilibrés',
          examples: ['Merlot', 'Sangiovese', 'Tempranillo'],
          characteristics: 'Tanins modérés, structure équilibrée, fruits mûrs',
          serving: '14-16°C',
          aging: '3-8 ans',
          food: 'Viandes rouges, plats en sauce, fromages affinés'
        },
        {
          name: 'Puissants et Complexes',
          examples: ['Cabernet Sauvignon', 'Syrah', 'Nebbiolo'],
          characteristics: 'Tanins fermes, structure imposante, notes de cuir et épices',
          serving: '16-18°C',
          aging: '8-20 ans',
          food: 'Viandes rouges grillées, gibier, fromages forts'
        }
      ]
    },
    {
      category: 'Vins Blancs',
      icon: <Drop size={32} color="#4B164C" weight="light" />,
      description: 'Vins élaborés à partir de raisins blancs ou rouges sans macération',
      styles: [
        {
          name: 'Secs et Minéraux',
          examples: ['Chablis', 'Sancerre', 'Muscadet'],
          characteristics: 'Acidité vive, notes minérales, fraîcheur',
          serving: '8-10°C',
          aging: '2-5 ans',
          food: 'Fruits de mer, poissons, fromages frais'
        },
        {
          name: 'Aromatiques et Fruités',
          examples: ['Riesling', 'Sauvignon Blanc', 'Gewürztraminer'],
          characteristics: 'Aromes intenses, fruits exotiques, épices',
          serving: '10-12°C',
          aging: '3-8 ans',
          food: 'Cuisine asiatique, poissons gras, fromages doux'
        },
        {
          name: 'Riches et Complexes',
          examples: ['Chardonnay', 'Viognier', 'Sémillon'],
          characteristics: 'Corps plein, notes de beurre, complexité aromatique',
          serving: '12-14°C',
          aging: '5-15 ans',
          food: 'Crustacés, volailles en sauce, fromages affinés'
        }
      ]
    },
    {
      category: 'Vins Rosés',
      icon: <Palette size={32} color="#4B164C" weight="light" />,
      description: 'Vins élaborés avec une courte macération des peaux rouges',
      styles: [
        {
          name: 'Légers et Frais',
          examples: ['Provence', 'Côtes de Provence', 'Tavel'],
          characteristics: 'Couleur pâle, acidité vive, fruits rouges frais',
          serving: '8-10°C',
          aging: '1-2 ans',
          food: 'Salades, apéritifs, cuisine méditerranéenne'
        },
        {
          name: 'Structurés et Fruités',
          examples: ['Bandol', 'Côtes du Rhône rosé', 'Languedoc'],
          characteristics: 'Couleur plus soutenue, structure, fruits mûrs',
          serving: '10-12°C',
          aging: '2-4 ans',
          food: 'Grillades, plats épicés, fromages doux'
        }
      ]
    },
    {
      category: 'Vins Effervescents',
      icon: <Sparkle size={32} color="#4B164C" weight="light" />,
      description: 'Vins avec bulles naturelles ou ajoutées',
      styles: [
        {
          name: 'Champagne et Méthode Traditionnelle',
          examples: ['Champagne', 'Crémant', 'Cava'],
          characteristics: 'Bulles fines, complexité, notes de brioche',
          serving: '6-8°C',
          aging: '3-10 ans',
          food: 'Apéritifs, fruits de mer, desserts'
        },
        {
          name: 'Prosecco et Charmat',
          examples: ['Prosecco', 'Asti', 'Lambrusco'],
          characteristics: 'Bulles plus grosses, fruits frais, douceur',
          serving: '6-8°C',
          aging: '1-3 ans',
          food: 'Apéritifs, desserts, cuisine italienne'
        }
      ]
    },
    {
      category: 'Vins Doux et Liquoreux',
      icon: <Thermometer size={32} color="#4B164C" weight="light" />,
      description: 'Vins avec sucre résiduel naturel ou concentré',
      styles: [
        {
          name: 'Vins de Botrytis',
          examples: ['Sauternes', 'Tokaji', 'Beerenauslese'],
          characteristics: 'Sucre concentré, notes de miel, fruits confits',
          serving: '8-10°C',
          aging: '10-50 ans',
          food: 'Foie gras, desserts, fromages bleus'
        },
        {
          name: 'Vins Fortifiés',
          examples: ['Porto', 'Sherry', 'Madeira'],
          characteristics: 'Alcool élevé, oxydation, complexité',
          serving: '12-16°C',
          aging: '10-100 ans',
          food: 'Desserts, fromages forts, chocolat'
        }
      ]
    }
  ];

  const wineCharacteristics = [
    {
      name: 'Corps',
      description: 'La sensation de poids et de texture en bouche',
      levels: [
        { name: 'Léger', description: 'Fluide et rafraîchissant' },
        { name: 'Moyen', description: 'Équilibré et harmonieux' },
        { name: 'Plein', description: 'Riche et enveloppant' }
      ]
    },
    {
      name: 'Acidité',
      description: 'La fraîcheur et la vivacité du vin',
      levels: [
        { name: 'Faible', description: 'Doux et rond' },
        { name: 'Moyenne', description: 'Équilibrée' },
        { name: 'Élevée', description: 'Vive et rafraîchissante' }
      ]
    },
    {
      name: 'Tanins',
      description: 'La sensation d\'astringence et de structure',
      levels: [
        { name: 'Doux', description: 'Souple et accessible' },
        { name: 'Modérés', description: 'Équilibrés' },
        { name: 'Fermes', description: 'Structurés et complexes' }
      ]
    },
    {
      name: 'Alcool',
      description: 'La chaleur et la puissance du vin',
      levels: [
        { name: 'Faible (10-12%)', description: 'Léger et rafraîchissant' },
        { name: 'Moyen (12-14%)', description: 'Équilibré' },
        { name: 'Élevé (14%+)', description: 'Puissant et chaleureux' }
      ]
    }
  ];

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#4B164C', 
          marginBottom: '10px',
          fontWeight: '700'
        }}>
          Styles de Vin
        </h1>
        <p style={{ 
          fontSize: '1.2rem', 
          color: '#666', 
          maxWidth: '700px', 
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Découvrez les différents styles de vin, leurs caractéristiques et leurs accords mets-vins
        </p>
      </div>

      {/* Wine Styles Categories */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Catégories de Vins
        </h2>
        
        {wineStyles.map((category, categoryIndex) => (
          <div key={categoryIndex} style={{ marginBottom: '40px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px', 
              marginBottom: '20px',
              padding: '20px',
              background: '#f8f6fa',
              borderRadius: '12px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '60px',
                height: '60px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                {category.icon}
              </div>
              <div>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  color: '#4B164C', 
                  margin: '0 0 8px 0',
                  fontWeight: '600'
                }}>
                  {category.category}
                </h3>
                <p style={{ 
                  color: '#666', 
                  fontSize: '1rem',
                  margin: '0',
                  lineHeight: '1.5'
                }}>
                  {category.description}
                </p>
              </div>
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
              gap: '20px' 
            }}>
              {category.styles.map((style, styleIndex) => (
                <div
                  key={styleIndex}
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    border: '2px solid #e8e0e8',
                    padding: '24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <h4 style={{ 
                    fontSize: '1.2rem', 
                    color: '#4B164C', 
                    margin: '0 0 16px 0',
                    fontWeight: '600'
                  }}>
                    {style.name}
                  </h4>

                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{ 
                      fontSize: '0.9rem', 
                      color: '#4B164C', 
                      margin: '0 0 8px 0',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Exemples
                    </h5>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: '6px' 
                    }}>
                      {style.examples.map((example, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: '4px 8px',
                            background: '#f0e6f0',
                            borderRadius: '12px',
                            fontSize: '0.8rem',
                            color: '#4B164C'
                          }}
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <h5 style={{ 
                      fontSize: '0.9rem', 
                      color: '#4B164C', 
                      margin: '0 0 8px 0',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Caractéristiques
                    </h5>
                    <p style={{ 
                      color: '#666', 
                      fontSize: '0.9rem',
                      margin: '0',
                      lineHeight: '1.4'
                    }}>
                      {style.characteristics}
                    </p>
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <h6 style={{ 
                        fontSize: '0.8rem', 
                        color: '#4B164C', 
                        margin: '0 0 4px 0',
                        fontWeight: '500'
                      }}>
                        Service
                      </h6>
                      <p style={{ 
                        color: '#666', 
                        fontSize: '0.8rem',
                        margin: '0'
                      }}>
                        {style.serving}
                      </p>
                    </div>
                    <div>
                      <h6 style={{ 
                        fontSize: '0.8rem', 
                        color: '#4B164C', 
                        margin: '0 0 4px 0',
                        fontWeight: '500'
                      }}>
                        Garde
                      </h6>
                      <p style={{ 
                        color: '#666', 
                        fontSize: '0.8rem',
                        margin: '0'
                      }}>
                        {style.aging}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h5 style={{ 
                      fontSize: '0.9rem', 
                      color: '#4B164C', 
                      margin: '0 0 8px 0',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Accords Mets-Vins
                    </h5>
                    <p style={{ 
                      color: '#666', 
                      fontSize: '0.9rem',
                      margin: '0',
                      lineHeight: '1.4'
                    }}>
                      {style.food}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Wine Characteristics */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Caractéristiques du Vin
        </h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px' 
        }}>
          {wineCharacteristics.map((characteristic, index) => (
            <div
              key={index}
              style={{
                background: '#fff',
                borderRadius: '12px',
                border: '2px solid #e8e0e8',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ 
                fontSize: '1.3rem', 
                color: '#4B164C', 
                margin: '0 0 8px 0',
                fontWeight: '600'
              }}>
                {characteristic.name}
              </h3>
              <p style={{ 
                color: '#666', 
                fontSize: '0.95rem',
                margin: '0 0 20px 0',
                lineHeight: '1.5'
              }}>
                {characteristic.description}
              </p>
              
              <div style={{ display: 'grid', gap: '12px' }}>
                {characteristic.levels.map((level, levelIndex) => (
                  <div
                    key={levelIndex}
                    style={{
                      padding: '12px',
                      background: '#f8f6fa',
                      borderRadius: '8px',
                      border: '1px solid #e8e0e8'
                    }}
                  >
                    <h4 style={{ 
                      fontSize: '1rem', 
                      color: '#4B164C', 
                      margin: '0 0 4px 0',
                      fontWeight: '500'
                    }}>
                      {level.name}
                    </h4>
                    <p style={{ 
                      color: '#666', 
                      fontSize: '0.9rem',
                      margin: '0',
                      lineHeight: '1.4'
                    }}>
                      {level.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Links */}
      <div style={{ 
        padding: '30px', 
        background: '#f8f6fa', 
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <h3 style={{ 
          fontSize: '1.3rem', 
          color: '#4B164C', 
          marginBottom: '15px',
          fontWeight: '600'
        }}>
          Continuez à Explorer
        </h3>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          justifyContent: 'center' 
        }}>
          <Link
            to="/info/countries"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#4B164C',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
            onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
          >
            Pays Viticoles
            <CaretRight size={16} />
          </Link>
          <Link
            to="/info/cepages"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#6d2c7a',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
            onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
          >
            Cépages
            <CaretRight size={16} />
          </Link>
          <Link
            to="/info"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#f0e6f0',
              color: '#4B164C',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              border: '2px solid #4B164C',
              transition: 'all 0.2s'
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = '#4B164C';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = '#f0e6f0';
              e.currentTarget.style.color = '#4B164C';
            }}
          >
            Guide du Vin
            <CaretRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WineStylesPage; 